import React, { useState, useEffect } from "react";
import { FaTimes, FaEnvelope, FaLock, FaUser } from "react-icons/fa";

const API_BASE = process.env.REACT_APP_URL;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const THEME = "#636CCB";

export default function SignupModal({ onClose, openLogin }) {
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10000,
    },
    card: {
      width: 420,
      maxWidth: "92%",
      background: darkMode ? "#1e1e1e" : "#fff",
      borderRadius: 14,
      padding: "30px 32px",
      fontFamily: "Inter, Arial",
      position: "relative",
      boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
      color: darkMode ? "#eee" : "#333",
    },
    fieldWrap: {
      position: "relative",
      marginBottom: 14,
    },
    iconStyle: {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      color: darkMode ? "#aaa" : "#666",
    },
    inputStyle: {
      width: "85%",
      padding: "12px 14px 12px 42px",
      borderRadius: 8,
      border: `1px solid ${darkMode ? "#444" : "#e6e6e6"}`,
      outline: "none",
      fontSize: 15,
      background: darkMode ? "#2d2d2d" : "#fff",
      color: darkMode ? "#eee" : "#333",
    },
    buttonPrimary: {
      width: "100%",
      background: THEME,
      color: "white",
      fontWeight: 700,
      border: "none",
      borderRadius: 8,
      padding: "12px",
      fontSize: 16,
      cursor: "pointer",
    },
    linkStyle: {
      color: THEME,
      fontWeight: 600,
      cursor: "pointer",
    },
  };
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [confirm, setConfirm] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // OTP-related state
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Countdown timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleSignup,
        });

        window.google.accounts.id.renderButton(
          document.getElementById("googleSignupBtn"),
          { theme: "outline", size: "large", width: "350" }
        );
      }
    };
  }, []);

  const handleGoogleSignup = (response) => {
    try {
      const decoded = JSON.parse(atob(response.credential.split(".")[1]));

      const googleEmail = decoded.email;
      const googleName = decoded.name;
      const googlePicture = decoded.picture;

      localStorage.setItem("googleEmail", googleEmail);
      localStorage.setItem("googleName", googleName);
      localStorage.setItem("googlePicture", googlePicture || "");

      fetch(`${API_BASE}/google-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: googleEmail,
          name: googleName,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          localStorage.setItem("userEmail", googleEmail);
          localStorage.setItem("userName", googleName);

          onClose();
          openLogin();
        })
        .catch((err) => console.error("Google Signup Error:", err));
    } catch (err) {
      console.error("Google Signup Error:", err);
    }
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).toLowerCase());

  const validateStrongPassword = (pass) => {
    const strongPassRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*_?&])[A-Za-z\d@$!%*_?&]{8,}$/;
    return strongPassRegex.test(pass);
  };

  const handleSendOtp = async () => {
    setError("");
    setSuccess("");

    if (!name || !email || !pass || !confirm)
      return setError("All fields are required");

    if (!consentChecked)
      return setError("You must agree to the consent terms to continue.");

    if (!validateEmail(email)) return setError("Enter a valid email");

    if (!validateStrongPassword(pass))
      return setError(
        "Password must include uppercase, lowercase, number, symbol and be at least 8 characters."
      );

    if (pass !== confirm) return setError("Passwords do not match");

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/send-signup-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "Failed to send OTP");
        setLoading(false);
        return;
      }

      setSuccess(data.message || "OTP sent to your email");
      setOtpSent(true);
      setShowOtpField(true);
      setResendTimer(300); // 5 minutes
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Server error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setSuccess("");

    if (!otp) return setError("Please enter the OTP");

    if (otp.length !== 6) return setError("OTP must be 6 digits");

    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/verify-signup-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          otp,
          username: name,
          password: pass,
          consent: {
            given: true,
            version: "v1.0",
            platform: "web"
          }
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || "OTP verification failed");
        setLoading(false);
        return;
      }

      setSuccess("Account created successfully!");
      setTimeout(() => {
        onClose();
        openLogin();
      }, 1200);
    } catch (err) {
      setError("Server error. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            right: 14,
            top: 14,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: darkMode ? "#fff" : "#000",
          }}
        >
          <FaTimes />
        </button>

        <h3 style={{ fontSize: 22, fontWeight: 700, textAlign: "center", color: darkMode ? "#fff" : "#000" }}>
          Create your account
        </h3>
        <p style={{ color: darkMode ? "#aaa" : "#666", textAlign: "center", marginBottom: 20 }}>
          Sign up and start using SourceCheck
        </p>

        {error && (
          <div style={{ color: "red", textAlign: "center", marginBottom: 10 }}>
            {error}
          </div>
        )}

        {success && (
          <div
            style={{ color: "green", textAlign: "center", marginBottom: 10 }}
          >
            {success}
          </div>
        )}

        <div style={styles.fieldWrap}>
          <FaUser style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="text"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div style={styles.fieldWrap}>
          <FaEnvelope style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div style={styles.fieldWrap}>
          <FaLock style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="password"
            placeholder="Password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
          />
        </div>

        <div style={styles.fieldWrap}>
          <FaLock style={styles.iconStyle} />
          <input
            style={styles.inputStyle}
            type="password"
            placeholder="Confirm password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {/* Consent Section */}
        <div
          style={{
            marginTop: 10,
            marginBottom: 20,
            padding: "10px",
            borderRadius: 8,
            border: `1px solid ${darkMode ? "#444" : "#eee"}`,
            background: darkMode ? "#2d2d2d" : "#f9f9f9",
            fontSize: 13,
            color: darkMode ? "#aaa" : "#555",
            lineHeight: 1.6,
          }}
        >
          {/* Consent Text */}
          <div
            style={{
              textAlign: "justify",
              marginBottom: 10,
            }}
          >
            By signing up, I agree that <b>SourceCheck</b> may collect, store, and process
            my personal information for account creation, verification, communication,
            and service-related activities. I understand that my data will be handled
            securely and in accordance with applicable data protection laws.
          </div>

          {/* Checkbox */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              fontWeight: 500,
              color: darkMode ? "#eee" : "#333"
            }}
          >
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
            />
            <span>I agree</span>
          </label>
        </div>

        {/* OTP Input Field - Only show after OTP is sent */}
        {showOtpField && (
          <div style={{ marginBottom: 14 }}>
            <div style={styles.fieldWrap}>
              <FaEnvelope style={styles.iconStyle} />
              <input
                style={styles.inputStyle}
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
              />
            </div>

            {/* Timer and Resend Button */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 8,
              fontSize: 13
            }}>
              <span style={{ color: darkMode ? "#aaa" : "#666" }}>
                {resendTimer > 0 ? (
                  <>
                    OTP expires in {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, '0')}
                  </>
                ) : (
                  <span style={{ color: "#ff4444" }}>OTP expired</span>
                )}
              </span>
              <button
                onClick={handleResendOtp}
                disabled={resendTimer > 0 || loading}
                style={{
                  background: "none",
                  border: "none",
                  color: resendTimer > 0 ? (darkMode ? "#555" : "#ccc") : THEME,
                  fontWeight: 600,
                  cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                  fontSize: 13,
                  textDecoration: resendTimer > 0 ? "none" : "underline"
                }}
              >
                Resend OTP
              </button>
            </div>
          </div>
        )}
        <button
          style={{ ...styles.buttonPrimary, marginTop: -10 }}
          onClick={otpSent ? handleVerifyOtp : handleSendOtp}
          disabled={loading}
        >
          {loading ? "Processing..." : otpSent ? "Verify & Create Account" : "Send OTP"}
        </button>


        <div style={{ textAlign: "center", marginTop: 14, color: darkMode ? "#aaa" : "#666" }}>
          Already have an account?{" "}
          <span
            style={styles.linkStyle}
            onClick={() => {
              onClose();
              openLogin();
            }}
          >
            Sign in
          </span>
        </div>

        <div
          style={{ marginTop: 18, display: "flex", justifyContent: "center" }}
        >
          <div id="googleSignupBtn"></div>
        </div>
      </div>
    </div>
  );
}
